import { toastError } from "@/lib/toast-error";
import { useState } from "react";
import { useParams, Link } from "wouter";
import {
  useGetRegistration,
  useUpdateRegistrationStatus,
  useListTemplates,
  useSendRegistrationFollowUp,
  usePreviewRegistrationFollowUp,
  useNotifyRepForRegistration,
  useApplyReplySuggestion,
  RegistrationStatus,
  type SupplierReply,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, Building2, User, Mail, Clock, RefreshCw, Loader2, Send, Bell, MessageSquare, CheckCircle2, XCircle, HelpCircle, ChevronDown, ChevronUp } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDate, formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { useQueryClient } from "@tanstack/react-query";

export default function RegistrationDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: reg, isLoading, refetch } = useGetRegistration(id || "", {
    query: { enabled: !!id, queryKey: ["/api/registrations", id] }
  });

  const updateStatus = useUpdateRegistrationStatus();
  
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<RegistrationStatus>(RegistrationStatus.sent);
  const [updateNotes, setUpdateNotes] = useState("");

  const handleUpdateStatus = () => {
    if (!id) return;
    updateStatus.mutate(
      { id, data: { status: newStatus, notes: updateNotes || undefined } },
      {
        onSuccess: () => {
          toast.success("Status updated successfully");
          setIsUpdateOpen(false);
          setUpdateNotes("");
          refetch();
          queryClient.invalidateQueries({ queryKey: ["/api/dashboard/summary"] });
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to update status");
        }
      }
    );
  };

  const openUpdateDialog = () => {
    if (reg) setNewStatus(reg.status);
    setIsUpdateOpen(true);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-5xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link href="/registrations">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">Registration</h1>
              {reg && <StatusBadge status={reg.status} />}
              {reg && reg.replies && reg.replies.length > 0 && (
                <Badge variant="secondary" className="gap-1 text-xs">
                  <MessageSquare className="h-3 w-3" />
                  {reg.replies.length} {reg.replies.length === 1 ? "reply" : "replies"}
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">{reg?.dealOpportunityName || "Loading..."}</p>
          </div>
        </div>
        
        {reg && (
          <div className="flex items-center gap-2">
            <FollowUpDialog registrationId={reg.id} onSent={() => refetch()} />
            <NotifyRepButton registrationId={reg.id} onNotified={() => refetch()} />
          <Dialog open={isUpdateOpen} onOpenChange={setIsUpdateOpen}>
            <DialogTrigger asChild>
              <Button onClick={openUpdateDialog}>
                <RefreshCw className="mr-2 h-4 w-4" /> Update Status
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Update Registration Status</DialogTitle>
                <DialogDescription>
                  Manually update the status of this registration based on supplier response.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={newStatus} onValueChange={(v) => setNewStatus(v as RegistrationStatus)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {([
                        RegistrationStatus.sent,
                        RegistrationStatus.accepted,
                        RegistrationStatus.bid,
                        RegistrationStatus.no_bid,
                        RegistrationStatus.more_info,
                        RegistrationStatus.expired,
                      ] as RegistrationStatus[]).map(s => (
                        <SelectItem key={s} value={s}>{s.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Textarea 
                    placeholder="E.g., Supplier requested more info regarding user count..." 
                    value={updateNotes}
                    onChange={(e) => setUpdateNotes(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsUpdateOpen(false)}>Cancel</Button>
                <Button onClick={handleUpdateStatus} disabled={updateStatus.isPending}>
                  {updateStatus.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Update
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : reg ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Supplier replies with suggestions */}
            {reg.replies && reg.replies.length > 0 && (
              <div className="space-y-3">
                {reg.replies.map((reply) => (
                  <ReplyCard
                    key={reply.id}
                    reply={reply}
                    registrationId={reg.id}
                    onApplied={() => refetch()}
                  />
                ))}
              </div>
            )}

            <Card>
              <CardHeader className="pb-4 border-b bg-muted/20">
                <CardTitle className="text-lg">Email Content</CardTitle>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-2 font-medium text-muted-foreground text-right">From:</div>
                    <div className="col-span-10">{reg.senderName} &lt;{reg.senderEmail}&gt;</div>
                  </div>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-2 font-medium text-muted-foreground text-right">To:</div>
                    <div className="col-span-10">{reg.supplierContactName} &lt;{reg.supplierContactEmail}&gt;</div>
                  </div>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-2 font-medium text-muted-foreground text-right">Date:</div>
                    <div className="col-span-10">{formatDateTime(reg.sentAt)}</div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 mt-4 pt-4 border-t">
                    <div className="col-span-2 font-medium text-muted-foreground text-right">Subject:</div>
                    <div className="col-span-10 font-medium">{reg.subject || "—"}</div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="font-mono text-sm whitespace-pre-wrap text-foreground bg-muted/10 p-4 rounded-md border">
                  {reg.body || "No email body available."}
                </div>
              </CardContent>
            </Card>

            {reg.mergedFields && Object.keys(reg.mergedFields).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Extra Fields</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(reg.mergedFields).map(([key, val]) => (
                      <div key={key} className="bg-muted/30 p-3 rounded-lg border">
                        <div className="text-xs text-muted-foreground mb-1">{key}</div>
                        <div className="font-medium">{String(val)}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-xs text-muted-foreground mb-1 uppercase tracking-wider font-semibold">Deal</div>
                  <Link href={`/deals/${reg.dealId}`} className="text-primary hover:underline font-medium flex items-center gap-1">
                    {reg.dealOpportunityName}
                  </Link>
                  <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <Building2 className="h-3 w-3" /> {reg.dealAccountName}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <div className="text-xs text-muted-foreground mb-1 uppercase tracking-wider font-semibold">Supplier</div>
                  <div className="font-medium flex items-center gap-1">
                    <Building2 className="h-4 w-4 text-muted-foreground" /> {reg.supplierAccountName}
                  </div>
                  <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <User className="h-3 w-3" /> {reg.supplierContactName}
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <div className="text-xs text-muted-foreground mb-1 uppercase tracking-wider font-semibold">Template</div>
                  <div className="font-medium">{reg.templateName || "—"}</div>
                  <div className="inline-block px-2 py-1 bg-secondary text-secondary-foreground rounded text-xs mt-2">
                    {reg.technology || "Unknown"}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" /> Timeline
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {reg.events && reg.events.length > 0 ? (
                    reg.events.map((event, i) => (
                      <div key={event.id} className="relative pl-6 pb-2">
                        {i !== reg.events.length - 1 && (
                          <div className="absolute left-[9px] top-5 bottom-0 w-[2px] bg-border" />
                        )}
                        <div className={`absolute left-0 top-1 h-5 w-5 rounded-full border-2 bg-background z-10 ${event.type === "reply_received" ? "border-blue-500" : "border-primary"}`} />
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{event.message}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{formatDateTime(event.createdAt)}</span>
                            {event.actorEmail && (
                              <>
                                <span>•</span>
                                <span>{event.actorEmail}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-muted-foreground text-center py-4">No events found.</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-destructive font-medium">Registration not found.</div>
      )}
    </div>
  );
}

const SUGGESTED_STATUS_LABELS: Record<string, { label: string; icon: React.ReactNode; className: string }> = {
  approved: {
    label: "Approved",
    icon: <CheckCircle2 className="h-4 w-4" />,
    className: "text-green-700 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-950/30 dark:border-green-900",
  },
  rejected: {
    label: "Rejected",
    icon: <XCircle className="h-4 w-4" />,
    className: "text-red-700 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-950/30 dark:border-red-900",
  },
  more_info: {
    label: "Needs More Info",
    icon: <HelpCircle className="h-4 w-4" />,
    className: "text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-400 dark:bg-amber-950/30 dark:border-amber-900",
  },
  unclear: {
    label: "Unclear",
    icon: <HelpCircle className="h-4 w-4" />,
    className: "text-muted-foreground bg-muted/50 border-border",
  },
};

const STATUS_TO_REGISTRATION: Record<string, RegistrationStatus> = {
  approved: RegistrationStatus.accepted,
  rejected: RegistrationStatus.no_bid,
  more_info: RegistrationStatus.more_info,
};

function ReplyCard({
  reply,
  registrationId,
  onApplied,
}: {
  reply: SupplierReply;
  registrationId: string;
  onApplied: () => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const [overrideStatus, setOverrideStatus] = useState<string>("");
  const applyMutation = useApplyReplySuggestion();
  const queryClient = useQueryClient();

  const suggested = reply.suggestedStatus;
  const suggestionInfo = suggested ? SUGGESTED_STATUS_LABELS[suggested] : undefined;
  const defaultApplyStatus = suggested ? STATUS_TO_REGISTRATION[suggested] ?? "" : "";

  const handleApply = (statusOverride?: string) => {
    const statusToApply = statusOverride ?? overrideStatus ?? defaultApplyStatus;
    applyMutation.mutate(
      { id: registrationId, replyId: reply.id, data: { status: statusToApply || undefined } },
      {
        onSuccess: () => {
          toast.success("Status updated from supplier reply");
          queryClient.invalidateQueries({ queryKey: ["/api/registrations", registrationId] });
          onApplied();
        },
        onError: (err: unknown) => toastError(err, "Failed to apply suggestion"),
      },
    );
  };

  return (
    <Card className="border-blue-200 dark:border-blue-900 bg-blue-50/30 dark:bg-blue-950/10" data-testid="reply-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <CardTitle className="text-sm font-semibold">
                Reply from {reply.fromName ? `${reply.fromName} <${reply.fromAddress}>` : reply.fromAddress}
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">
                {formatDateTime(reply.receivedAt)}
                {reply.subject && ` · ${reply.subject}`}
              </CardDescription>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </Button>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 space-y-4">
          {reply.snippet && (
            <div className="text-sm bg-background border rounded-md p-3 text-muted-foreground italic">
              "{reply.snippet}"
            </div>
          )}

          {suggestionInfo && (
            <div className={`flex items-start gap-3 rounded-md border p-3 ${suggestionInfo.className}`}>
              <div className="flex-shrink-0 mt-0.5">{suggestionInfo.icon}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">
                  Suggested status: <span className="font-semibold">{suggestionInfo.label}</span>
                </p>
                <p className="text-xs opacity-80 mt-0.5">Based on automatic keyword analysis of the reply content.</p>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
            <div className="flex-1 space-y-1">
              <Label className="text-xs">Apply as status</Label>
              <Select
                value={overrideStatus || defaultApplyStatus}
                onValueChange={setOverrideStatus}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select status…" />
                </SelectTrigger>
                <SelectContent>
                  {[
                    RegistrationStatus.accepted,
                    RegistrationStatus.bid,
                    RegistrationStatus.no_bid,
                    RegistrationStatus.more_info,
                    RegistrationStatus.sent,
                  ].map((s) => (
                    <SelectItem key={s} value={s} className="text-xs">
                      {s.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              size="sm"
              onClick={() => handleApply(overrideStatus || defaultApplyStatus || undefined)}
              disabled={applyMutation.isPending || (!overrideStatus && !defaultApplyStatus)}
              data-testid="btn-apply-reply"
            >
              {applyMutation.isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
              Apply
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function FollowUpDialog({ registrationId, onSent }: { registrationId: string; onSent: () => void }) {
  const [open, setOpen] = useState(false);
  const [templateId, setTemplateId] = useState<string>("");
  const [preview, setPreview] = useState<{ subject: string; body: string } | null>(null);

  const { data: templatesData } = useListTemplates({ purpose: "follow_up" }, {
    query: { enabled: open, queryKey: ["/api/templates", { purpose: "follow_up" }] },
  });
  const previewMut = usePreviewRegistrationFollowUp();
  const sendMut = useSendRegistrationFollowUp();

  const templates = templatesData?.results ?? [];

  const handlePreview = (id: string) => {
    setTemplateId(id);
    setPreview(null);
    if (!id) return;
    previewMut.mutate(
      { id: registrationId, data: { templateId: id } },
      {
        onSuccess: (res) => setPreview({ subject: res.subject, body: res.body }),
        onError: (err: unknown) => toastError(err, "Failed to preview follow-up"),
      },
    );
  };

  const handleSend = () => {
    if (!templateId) return;
    sendMut.mutate(
      { id: registrationId, data: { templateId } },
      {
        onSuccess: () => {
          toast.success("Follow-up sent");
          setOpen(false);
          setTemplateId("");
          setPreview(null);
          onSent();
        },
        onError: (err: unknown) => toastError(err, "Failed to send follow-up"),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="btn-follow-up">
          <Send className="mr-2 h-4 w-4" /> Send Follow-up
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Send Follow-up to Supplier</DialogTitle>
          <DialogDescription>
            Send a nudge using a follow-up template. Sent through your connected mailbox.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Follow-up Template</Label>
            <Select value={templateId} onValueChange={handlePreview}>
              <SelectTrigger data-testid="follow-up-template-select">
                <SelectValue placeholder={templates.length ? "Select a template" : "No follow-up templates available"} />
              </SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {previewMut.isPending && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
            </div>
          )}
          {preview && (
            <div className="space-y-2 rounded-md border bg-muted/20 p-3" data-testid="follow-up-preview">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Subject</div>
              <div className="text-sm font-medium">{preview.subject}</div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mt-3">Body</div>
              <div className="font-mono text-xs whitespace-pre-wrap">{preview.body}</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSend}
            disabled={!templateId || sendMut.isPending || previewMut.isPending}
            data-testid="btn-send-follow-up"
          >
            {sendMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Send Follow-up
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NotifyRepButton({ registrationId, onNotified }: { registrationId: string; onNotified: () => void }) {
  const notifyMut = useNotifyRepForRegistration();
  const handleClick = () => {
    notifyMut.mutate(
      { id: registrationId },
      {
        onSuccess: () => {
          toast.success("Internal notification sent to deal owner");
          onNotified();
        },
        onError: (err: unknown) => toastError(err, "Failed to notify rep"),
      },
    );
  };
  return (
    <Button variant="outline" onClick={handleClick} disabled={notifyMut.isPending} data-testid="btn-notify-rep">
      {notifyMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bell className="mr-2 h-4 w-4" />}
      Notify Rep
    </Button>
  );
}
